import { AfterViewInit, ChangeDetectionStrategy, Component, Inject, OnDestroy, ViewChild, ViewEncapsulation } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { MatLegacyDialogRef as MatDialogRef, MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA } from '@angular/material/legacy-dialog';
import { Unsubscriber } from '@xpo-ltl/ngx-ltl';
import { GetClaimResp } from '@xpo-ltl-2.0/sdk-claims';
import * as _ from 'lodash';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { takeUntil, finalize } from 'rxjs/operators';
import { FormUtils } from '../../classes/form-utils.class';
import { EmailsConfig } from '../../components/notes-emails/emails/emails-config';
import { EmailsComponent } from '../../components/notes-emails/emails/emails.component';
import { DeclinationEmailResults } from '../../enums';
import { EmailFormNames } from '../../enums/FormControlNames/email-form-names.enum';
import { FormStatus } from '../../enums/FormControlNames/form-status.enum';
import { DocumentSearch } from '@xpo-ltl/sdk-documentmanagement';

@Component({
  selector: 'app-declination-email',
  templateUrl: './declination-email.component.html',
  styleUrls: ['./declination-email.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DeclinationEmailComponent implements AfterViewInit, OnDestroy {
  private unsubscriber = new Unsubscriber();

  @ViewChild(EmailsComponent, {static: true}) emailsComponent;

  public readonly DeclinationEmailResults = DeclinationEmailResults;
  public readonly FormStatus = FormStatus;

  public dmsDocList$: Observable<DocumentSearch[]>;
  public claim$: Observable<GetClaimResp>;
  public emailsConfig: EmailsConfig;
  public claimsRegistrationFormGroup: UntypedFormGroup;
  public downloadDeclinationEmailAttachmentsFn: Function;

  private emailReadySubject = new BehaviorSubject<boolean>(false);
  public emailReady$ = this.emailReadySubject.asObservable();

  private isSendingEmailSubject = new BehaviorSubject<boolean>(false);
  public isSendingEmail$ = this.isSendingEmailSubject.asObservable();

  private validRecipientSubject = new BehaviorSubject<boolean>(true);
  public validRecipient$ = this.validRecipientSubject.asObservable();

  constructor(private dialogRef: MatDialogRef<DeclinationEmailComponent>, @Inject(MAT_DIALOG_DATA) public data: any) {
    this.emailsConfig = _.cloneDeep(_.get(data, 'emailsConfig', {}));
    this.claim$ = of(_.get(data, 'claim'));
    this.dmsDocList$ = of(_.get(data, 'dmsDocListObs'));
    this.claimsRegistrationFormGroup = _.get(data, 'claimsRegistrationFormGroup');
    this.downloadDeclinationEmailAttachmentsFn = _.get(data, 'downloadDMSAttachmentFn');
  }

  public ngAfterViewInit() {
    this.emailsComponent.formGroup.statusChanges.pipe(takeUntil(this.unsubscriber.done)).subscribe(status => {
      this.emailReadySubject.next(status === FormStatus.Valid);
    });

    setTimeout(() => {
      this.emailReadySubject.next(this.emailsComponent.formGroup.valid);
    });

    const recipientControl = FormUtils.getNestedControl(this.emailsComponent.formGroup, EmailFormNames.Recipient) as UntypedFormControl;
    if (recipientControl) {
      recipientControl.statusChanges.pipe(takeUntil(this.unsubscriber.done)).subscribe(status => {
        this.validRecipientSubject.next(status === FormStatus.Valid);
      });
    }
  }

  public ngOnDestroy() {
    this.unsubscriber.complete();
  }

  public handleSend() {
    // tell email control to send the email
    this.isSendingEmailSubject.next(true);
    this.emailsComponent
      .sendEmail()
      .pipe(
        finalize(() => {
          this.isSendingEmailSubject.next(false);
        })
      )
      .subscribe(() => {
        this.dialogRef.close(DeclinationEmailResults.Sent);
      });
  }
}
